import React, { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { getUserData, logoutAPI } from "../../utils/api";
import { showConfirmation } from "../../utils/sweetAlert";
import "./AdminLayout.css";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ path: "/admin/dashboard", label: "Dashboard", icon: "bi bi-grid-1x2" }],
  },
  {
    label: "Content",
    items: [
      { path: "/admin/test-series", label: "Test Series", icon: "bi bi-journal-text" },
      { path: "/admin/courses", label: "Courses", icon: "bi bi-mortarboard" },
    ],
  },
  {
    label: "People",
    items: [
      { path: "/admin/users", label: "Users", icon: "bi bi-people" },
      { path: "/admin/subscriptions", label: "Subscriptions", icon: "bi bi-credit-card-2-front" },
    ],
  },
  {
    label: "Insights",
    items: [{ path: "/admin/results/leaderboard", label: "Leaderboard", icon: "bi bi-trophy" }],
  },
  {
    label: "Account",
    items: [{ path: "/admin/settings", label: "Settings", icon: "bi bi-gear" }],
  },
];

// Title + breadcrumb for the top bar, derived from the current route
const getRouteMeta = (pathname) => {
  const rules = [
    [/\/test-series\/create$/, "Create Test Series", ["Test Series", "Create"]],
    [/\/test-series\/[^/]+\/edit$/, "Edit Test Series", ["Test Series", "Edit"]],
    [/\/test-series\/[^/]+\/sections$/, "Sections", ["Test Series", "Sections"]],
    [/\/section\/[^/]+\/add-question$/, "Add Question", ["Test Series", "Sections", "Add Question"]],
    [/\/section\/[^/]+\/edit-question\//, "Edit Question", ["Test Series", "Sections", "Edit Question"]],
    [/\/section\/[^/]+\/questions$/, "Questions", ["Test Series", "Sections", "Questions"]],
    [/\/test-series$/, "Test Series", ["Content", "Test Series"]],
    [/\/dashboard$/, "Dashboard", ["Overview"]],
    [/\/courses$/, "Courses", ["Content", "Courses"]],
    [/\/users$/, "Users", ["People", "Users"]],
    [/\/subscriptions$/, "Subscriptions", ["People", "Subscriptions"]],
    [/\/results\/leaderboard$/, "Leaderboard", ["Insights", "Leaderboard"]],
    [/\/settings$/, "Settings", ["Account", "Settings"]],
  ];
  const match = rules.find(([re]) => re.test(pathname));
  return match ? { title: match[1], crumbs: match[2] } : { title: "Admin", crumbs: [] };
};

const readCollapsed = () => {
  try {
    return localStorage.getItem("sidebarCollapsed") === "1";
  } catch {
    return false;
  }
};

function AdminLayout({ onLogout }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const userData = getUserData();
  const { title, crumbs } = getRouteMeta(location.pathname);

  const displayName = userData?.name || "Admin";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  // Close the drawer and user menu whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem("sidebarCollapsed", collapsed ? "1" : "0");
    } catch {
      // storage unavailable — preference just won't persist
    }
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setMenuOpen(false);
      }
    };
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("drawer-open", mobileOpen);
    return () => document.body.classList.remove("drawer-open");
  }, [mobileOpen]);

  useEffect(() => {
    document.title = `${title} · Jupiter Institute Admin`;
  }, [title]);

  const handleLogout = async () => {
    setMenuOpen(false);
    const result = await showConfirmation(
      "You'll need to sign in again to access the admin panel.",
      "Sign out?",
      "Sign out",
      "Stay signed in",
      "logout"
    );
    if (result.isConfirmed) {
      // Revoke the token on the server first; the browser is cleared either way
      await logoutAPI();
      onLogout();
    }
  };

  return (
    <div className={`admin-layout ${collapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-drawer-open" : ""}`}>
      <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />

      <aside className="sidebar" aria-label="Main navigation">
        <div className="sidebar-brand">
          <Link to="/admin/dashboard" className="brand-link">
            <img src="/logo.png" alt="Jupiter Institute" className="brand-logo" />
            <span className="brand-text">
              <strong>Jupiter Institute</strong>
              <small>Admin Console</small>
            </span>
          </Link>
          <button
            type="button"
            className="sidebar-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group) => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group-label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                  data-tooltip={item.label}
                >
                  <i className={`nav-icon ${item.icon}`}></i>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span className="avatar">{initials}</span>
            <span className="sidebar-user-text">
              <strong>{displayName}</strong>
              <small>{userData?.email || "Administrator"}</small>
            </span>
          </div>
          <button type="button" onClick={handleLogout} className="nav-link logout-link" data-tooltip="Sign out">
            <i className="nav-icon bi bi-box-arrow-right"></i>
            <span className="nav-label">Sign out</span>
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <button
            type="button"
            className="topbar-icon-btn menu-toggle-mobile"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <i className="bi bi-list"></i>
          </button>
          <button
            type="button"
            className="topbar-icon-btn menu-toggle-desktop"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <i className={`bi ${collapsed ? "bi-layout-sidebar" : "bi-layout-sidebar-inset"}`}></i>
          </button>

          <div className="topbar-title">
            {crumbs.length > 0 && (
              <div className="breadcrumbs">
                {crumbs.map((crumb, index) => (
                  <React.Fragment key={crumb}>
                    {index > 0 && <i className="bi bi-chevron-right"></i>}
                    <span>{crumb}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
            <h1>{title}</h1>
          </div>

          <div className="topbar-actions" ref={menuRef}>
            <button
              type="button"
              className={`user-chip ${menuOpen ? "open" : ""}`}
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="avatar">{initials}</span>
              <span className="user-chip-name">{displayName}</span>
              <i className="bi bi-chevron-down user-chip-caret"></i>
            </button>

            {menuOpen && (
              <div className="user-menu" role="menu">
                <div className="user-menu-header">
                  <span className="avatar avatar-lg">{initials}</span>
                  <div>
                    <strong>{displayName}</strong>
                    <small>{userData?.email || "Administrator"}</small>
                  </div>
                </div>
                <Link to="/admin/settings" className="user-menu-item" role="menuitem">
                  <i className="bi bi-shield-lock"></i> Account & security
                </Link>
                <button type="button" className="user-menu-item danger" onClick={handleLogout} role="menuitem">
                  <i className="bi bi-box-arrow-right"></i> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="content-area">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
